import { useCallback, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AdvanceTableResponsive from '../../../components/common/advance-table/AdvanceTableResponsive';
import CardDropdown from '../../../components/common/CardDropdown';
import SystemAlert from '../../../components/modal/SystemAlert';
import PendingListLoadingState from './common/PendingListLoadingState';
import useAdvanceTable from '../../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../../providers/AdvanceTableProvider';
import { useOrderContext } from '../../../providers/OrderProvider';
import { useCancelMyRejectedOrdersMutation } from '../../../slices/ordersApiSlice';
import { useCancelCustomerApplicationMutation } from '../../../slices/approvalsApiSlice';
import CustomerApplyInfoModal from './modal/CustomerApplyInfoModal';
import FinalCostApplyInfoModal from './modal/FinalCostApplyInfoModal';
import FinalCostReapplyModal from './modal/FinalCostReapplyModal';
import QuotaApplyInfoModal from './modal/QuotaApplyInfoModal';
import CustomerCreateApplyModal from '../../customer/CustomerCreateApplyModal';
import QuotaReapplyModal from '../../approval/QuotaReapplyModal';
import { getOrderDetailPath, getRejectedOrderEditPath, REJECTED_DETAIL_FROM_LIST, REJECTED_EDIT_FROM_APPLYING_PENDING } from '../../../utils/order/orderListPath';
import { savePendingApplyingReturnSnapshot } from '../../../utils/order/pendingApplyingReturnSnapshot';
import PendingListStatusBadge, { PendingThemeBadge } from './common/PendingListStatusBadge';
import PendingListEmptyState from './common/PendingListEmptyState';
import { PendingKeywordHighlight } from './common/PendingKeywordHighlight';
import formatCurrency from '../../../utils/formatCurrency';
import PendingListTableFooter from './common/PendingListTableFooter';
import PendingListToolbar from './common/PendingListToolbar';
import PendingOrderIdCell from './common/PendingOrderIdCell';
import { buildPendingApplyAtColumn, getPendingApplyAt } from './common/pendingApplyAtColumn';
import { getPendingApprovalKind } from './common/pendingApprovalTriggerSummary';
import { getPendingListStatusBadgeProps } from './common/pendingRowStatusDisplay';
import { getPendingListApplicant, isPendingListApplicantHighlighted } from '../../../utils/approval/approvalListHub';
import { PENDING_TAB_BADGE, PENDING_TABLE_PER_PAGE } from './common/pendingTableConfig';
import { PERM, hasPermission } from '../../../utils/accessPermissions';
import { usePendingKeywordFilter } from './common/usePendingKeywordFilter';
import { usePendingSearch } from './common/PendingSearchContext';

const REJECTED_TAB_BADGE = PENDING_TAB_BADGE.rejected;

const openRejectedEdit = (navigate, orderMongoId) => {
	navigate(getRejectedOrderEditPath(orderMongoId), {
		state: { from: REJECTED_EDIT_FROM_APPLYING_PENDING },
	});
};

const buildCustomerRenewInitialValues = (detail = {}) => ({
	newCustomerType: detail.newCustomerType || '',
	newCustomerCity: detail.newCustomerCity || '310000',
	newCustomerName: detail.newCustomerName || '',
	newCustomerCompany: detail.newCustomerOrganization || detail.newCustomerCompany || '',
	newCustomerDepartment: detail.newCustomerDepartment || '',
	newCustomerEmail: detail.newCustomerEmail || '',
	newCustomerGroup: detail.newCustomerGroup || '',
	newCustomerPhone: detail.newCustomerPhone || '',
	newCustomerAddress: detail.newCustomerAddress || '',
});

/** 申请侧：被驳回待办（订单 + 客户申请 + 额度申请，未取消） */
const RejectedOrderPendings = () => {
	const navigate = useNavigate();
	const { userInfo } = useSelector((state) => state.auth);
	const { rejectedPendingOrders = [], isLoadingRejectedPendingOrders, setCurrentOrderOIdState, refetchMyRejectedOrders } = useOrderContext();
	const { keyword, hasKeyword, filteredOrders } = usePendingKeywordFilter(rejectedPendingOrders);
	const { initialApplyingPageIndex } = usePendingSearch();

	const [cancelMyRejectedOrders, { isLoading: isCancellingOrder }] = useCancelMyRejectedOrdersMutation();
	const [cancelCustomerApplication, { isLoading: isCancellingCustomer }] = useCancelCustomerApplicationMutation();
	const isCancelling = isCancellingOrder || isCancellingCustomer;

	const [cancelTarget, setCancelTarget] = useState(null);
	const [showCancelConfirm, setShowCancelConfirm] = useState(false);
	const [renewCustomer, setRenewCustomer] = useState(null);
	const [showRenewModal, setShowRenewModal] = useState(false);
	const [customerApplyInfo, setCustomerApplyInfo] = useState(null);
	const [quotaApplyInfo, setQuotaApplyInfo] = useState(null);
	const [finalCostApplyInfo, setFinalCostApplyInfo] = useState(null);
	const [renewQuota, setRenewQuota] = useState(null);
	const [renewFinalCost, setRenewFinalCost] = useState(null);
	const canAdjustFinalCost = hasPermission(userInfo, PERM.ORDER_FINAL_COST_ADJUST);

	const displayOrders = useMemo(() => {
		const list = Array.isArray(filteredOrders) ? [...filteredOrders] : [];
		list.sort((a, b) => {
			const aTime = getPendingApplyAt(a);
			const bTime = getPendingApplyAt(b);
			return new Date(bTime || 0) - new Date(aTime || 0);
		});
		return list;
	}, [filteredOrders]);

	const prepareRejectedDetailNavigation = useCallback(
		(orderMongoId, pageIndex) => {
			if (!orderMongoId) return;
			savePendingApplyingReturnSnapshot({ keyword, pageIndex, tab: 'Rejected' });
			setCurrentOrderOIdState?.(orderMongoId);
		},
		[keyword, setCurrentOrderOIdState],
	);

	const requestCancel = useCallback((row) => {
		if (!row?._id) return;
		setCancelTarget(row);
		setShowCancelConfirm(true);
	}, []);

	const handleCancelConfirm = useCallback(async () => {
		if (!cancelTarget?._id) return;
		try {
			if (cancelTarget.entryKind === 'customer' || cancelTarget.entryKind === 'quota' || cancelTarget.entryKind === 'finalCost') {
				await cancelCustomerApplication(cancelTarget._id).unwrap();
			} else {
				await cancelMyRejectedOrders({ cancellingIds: [cancelTarget._id] }).unwrap();
			}
			toast.success('已取消申请，不再出现在被驳回待办');
			refetchMyRejectedOrders?.();
			setShowCancelConfirm(false);
			setCancelTarget(null);
		} catch (error) {
			toast.error(error?.data?.message || error?.error || '取消申请失败，请重试');
		}
	}, [cancelCustomerApplication, cancelMyRejectedOrders, cancelTarget, refetchMyRejectedOrders]);

	const openCustomerApplyInfo = useCallback((row) => {
		if (!row) return;
		setCustomerApplyInfo({
			row,
			approval: row.approval || {
				_id: row._id,
				title: row.title,
				status: 'reject',
				detail: { newCustomerConcatName: row.customerInfo?.customerName || '' },
				applicant: row.createdUser,
				approvalSuggestions: row.rejectReason || '',
				updatedAt: row.updatedAt,
			},
		});
	}, []);

	const openQuotaApplyInfo = useCallback((row) => {
		if (!row) return;
		setQuotaApplyInfo({
			row,
			approval: row.approval || {
				title: row.title,
				status: 'reject',
				detail: { customerName: row.customerInfo?.customerName || '' },
				applicant: row.createdUser,
				assistant: row.assistantUser,
				approvalSuggestions: row.rejectReason || '',
				_id: row._id,
			},
		});
	}, []);

	const openFinalCostApplyInfo = useCallback((row) => {
		if (!row) return;
		setFinalCostApplyInfo({
			approval: row.approval || {
				title: row.title,
				status: 'reject',
				detail: { orderId: row.orderId },
				applicant: row.createdUser,
				assistant: row.assistantUser,
				approvalSuggestions: row.rejectReason || '',
				_id: row._id,
			},
		});
	}, []);

	const openQuotaRenew = useCallback((row) => {
		const approval = row?.approval;
		if (!approval?._id) return;
		setRenewQuota(approval);
	}, []);

	const openCustomerRenew = useCallback(
		(row) => {
			const approval = row?.approval;
			if (!approval?._id) {
				navigate(`/approval/customer/detail/${row._id}`);
				return;
			}
			setRenewCustomer(approval);
			setShowRenewModal(true);
		},
		[navigate],
	);

	const renewApplicant = useMemo(() => {
		if (!renewCustomer?.applicant) return userInfo;
		const a = renewCustomer.applicant;
		return typeof a === 'object' ? a : { _id: a, name: renewCustomer.detail?.applicantName || '' };
	}, [renewCustomer, userInfo]);

	const renewInitialValues = useMemo(() => buildCustomerRenewInitialValues(renewCustomer?.detail), [renewCustomer?.detail]);

	const formatDateTime = useCallback((date) => moment(date).format('YYYY-MM-DD HH:mm'), []);

	const columns = useMemo(
		() => [
			{
				accessorKey: 'orderId',
				header: '编号 / 事项',
				meta: {
					headerProps: { className: 'sticky-column text-900 text-start' },
					cellProps: { className: 'fw-semibold text-start' },
				},
				cell: ({ row: { original }, table }) => {
					const { orderId, _id, entryKind, approval } = original;
					const pageIndex = table.getState().pagination?.pageIndex ?? 0;
					if (entryKind === 'customer' || entryKind === 'quota' || entryKind === 'finalCost') {
						const kind = getPendingApprovalKind(original);
						const applyQuota = entryKind === 'quota' ? approval?.detail?.applyQuota : null;
						const amountText = applyQuota != null && applyQuota !== '' ? formatCurrency(applyQuota) : '';
						return (
							<button
								type='button'
								className={`link-${REJECTED_TAB_BADGE} text-decoration-none border-0 bg-transparent p-0 fw-semibold text-start`}
								onClick={() => {
									if (entryKind === 'quota') openQuotaApplyInfo(original);
									else if (entryKind === 'finalCost') openFinalCostApplyInfo(original);
									else openCustomerApplyInfo(original);
								}}
							>
								<span className='d-flex flex-column align-items-start gap-1'>
									<span className='pending-approval-kind-line gap-2'>
										<PendingThemeBadge bg={kind.bg} text={kind.label} pill className='flex-shrink-0' />
										<span className='pending-approval-kind-subject' title={kind.subject || undefined}>
											<PendingKeywordHighlight text={kind.subject} />
										</span>
									</span>
									{amountText ? <span className='fs-11 fw-normal text-700'>申请额度 {amountText}</span> : null}
								</span>
							</button>
						);
					}
					const kind = getPendingApprovalKind(original);
					return (
						<div className='pending-approval-kind-line gap-2 flex-nowrap'>
							<PendingThemeBadge bg={kind.bg} text={kind.label} pill className='flex-shrink-0' />
							<PendingOrderIdCell
								orderId={orderId}
								orderMongoId={_id}
								badgeVariant={REJECTED_TAB_BADGE}
								to={getOrderDetailPath(_id)}
								linkState={{ from: REJECTED_DETAIL_FROM_LIST }}
								onOrderClick={() => prepareRejectedDetailNavigation(_id, pageIndex)}
								order={original}
								fullWidth={false}
							/>
						</div>
					);
				},
			},
			{
				accessorKey: 'customerInfo.customerName',
				header: '客户名称',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => (
					<span className={`link-${REJECTED_TAB_BADGE}`}>
						<PendingKeywordHighlight text={original.customerInfo?.customerName} />
					</span>
				),
			},
			{
				id: 'approvalStatus',
				accessorFn: () => '已驳回',
				header: '审批状态',
				meta: { headerProps: { className: 'pe-3 text-900' } },
				cell: () => {
					const config = getPendingListStatusBadgeProps('reject', REJECTED_TAB_BADGE, 'approval');
					return config ? <PendingListStatusBadge bg={config.bg} text={config.text} icon='circle-xmark' /> : '-';
				},
			},
			{
				id: 'saleName',
				accessorFn: (row) => {
					if (row?.entryKind === 'customer' || row?.entryKind === 'quota') return row?.createdUser?.name || '';
					return row?.saleUserInfo?.name || row?.saleUser?.name || '';
				},
				header: '销售员',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ getValue }) => <span className='fs-11'>{getValue() || '-'}</span>,
			},
			{
				id: 'assistantName',
				accessorFn: (row) => row?.assistantUser?.name || row?.assistantUserInfo?.name || '',
				header: '销售助理',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ getValue }) => <span className='fs-11'>{getValue() || '-'}</span>,
			},
			{
				id: 'applicantName',
				accessorFn: (row) => getPendingListApplicant(row)?.name || '',
				header: '申请人',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ row: { original } }) => {
					const applicant = getPendingListApplicant(original);
					return <span className={isPendingListApplicantHighlighted(original) ? 'fs-11 text-primary' : 'fs-11'}>{applicant?.name || '-'}</span>;
				},
			},
			{
				id: 'rejectReason',
				accessorFn: (row) => row.rejectReason || row.approval?.approvalSuggestions || '',
				header: '驳回原因',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ getValue }) => {
					const reason = String(getValue() || '').trim();
					return (
						<span className={`d-inline-block text-truncate text-start ${reason ? 'text-danger' : 'text-500'}`} style={{ maxWidth: '8rem' }} title={reason || undefined}>
							{reason || '未填写'}
						</span>
					);
				},
			},
			buildPendingApplyAtColumn(formatDateTime, '驳回时间'),
			{
				accessorKey: 'actions',
				header: '',
				enableSorting: false,
				meta: {
					headerProps: { className: 'text-end text-900' },
					cellProps: { className: 'text-end py-2' },
				},
				cell: ({ row: { original }, table }) => {
					const pageIndex = table.getState().pagination?.pageIndex ?? 0;
					if (original?.entryKind === 'finalCost') {
						return (
							<CardDropdown drop='start'>
								<div className='py-2'>
									<Dropdown.Item as='button' type='button' onClick={() => openFinalCostApplyInfo(original)}>
										查看
									</Dropdown.Item>
									{canAdjustFinalCost ? (
										<Dropdown.Item
											as='button'
											type='button'
											onClick={() =>
												setRenewFinalCost(
													original.approval || {
														_id: original._id,
														detail: {
															orderOID: original.approval?.detail?.orderOID,
															orderId: original.orderId,
															adjustedCost: original.approval?.detail?.adjustedCost,
															reason: original.approval?.detail?.reason,
														},
													},
												)
											}
										>
											编辑重提
										</Dropdown.Item>
									) : null}
									<Dropdown.Item as='button' type='button' onClick={() => requestCancel(original)}>
										取消申请
									</Dropdown.Item>
								</div>
							</CardDropdown>
						);
					}
					if (original?.entryKind === 'customer' || original?.entryKind === 'quota') {
						const openInfo = original.entryKind === 'quota' ? openQuotaApplyInfo : openCustomerApplyInfo;
						const openRenew = original.entryKind === 'quota' ? openQuotaRenew : openCustomerRenew;
						return (
							<CardDropdown drop='start'>
								<div className='py-2'>
									<Dropdown.Item as='button' type='button' onClick={() => openInfo(original)}>
										查看
									</Dropdown.Item>
									<Dropdown.Item as='button' type='button' onClick={() => openRenew(original)}>
										编辑重提
									</Dropdown.Item>
									<Dropdown.Item as='button' type='button' onClick={() => requestCancel(original)}>
										取消申请
									</Dropdown.Item>
								</div>
							</CardDropdown>
						);
					}
					return (
						<CardDropdown drop='start'>
							<div className='py-2'>
								<Dropdown.Item
									as={Link}
									to={getOrderDetailPath(original._id)}
									state={{ from: REJECTED_DETAIL_FROM_LIST }}
									onClick={() => prepareRejectedDetailNavigation(original._id, pageIndex)}
								>
									查看
								</Dropdown.Item>
								<Dropdown.Item as='button' type='button' onClick={() => openRejectedEdit(navigate, original._id)}>
									编辑重提
								</Dropdown.Item>
								<Dropdown.Item as='button' type='button' onClick={() => requestCancel(original)}>
									取消申请
								</Dropdown.Item>
							</div>
						</CardDropdown>
					);
				},
			},
		],
		[
			canAdjustFinalCost,
			formatDateTime,
			navigate,
			openCustomerApplyInfo,
			openCustomerRenew,
			openFinalCostApplyInfo,
			openQuotaApplyInfo,
			openQuotaRenew,
			prepareRejectedDetailNavigation,
			requestCancel,
		],
	);

	const table = useAdvanceTable({
		data: displayOrders,
		columns,
		sortable: true,
		pagination: true,
		perPage: PENDING_TABLE_PER_PAGE,
		initialState: {
			pagination: {
				pageIndex: Number.isInteger(initialApplyingPageIndex) && initialApplyingPageIndex >= 0 ? initialApplyingPageIndex : 0,
			},
			sorting: [{ id: 'applyAt', desc: true }],
		},
	});

	if (isLoadingRejectedPendingOrders) {
		return <PendingListLoadingState />;
	}

	const showFooter = rejectedPendingOrders.length > 0 || hasKeyword;
	const cancelLabel =
		cancelTarget?.entryKind === 'customer' || cancelTarget?.entryKind === 'quota' || cancelTarget?.entryKind === 'finalCost'
			? cancelTarget?.approval?.title ||
				cancelTarget?.title ||
				cancelTarget?.orderId ||
				(cancelTarget?.entryKind === 'quota' ? '该额度申请' : cancelTarget?.entryKind === 'finalCost' ? '该成本修正' : '该客户申请')
			: cancelTarget?.orderId || '该订单';

	return (
		<>
			<AdvanceTableProvider {...table}>
				<PendingListToolbar>
					{displayOrders.length === 0 ? (
						<PendingListEmptyState
							emptyTitle={hasKeyword ? '无匹配项' : '暂无被驳回'}
							emptyDescription={hasKeyword ? `当前待办中没有包含「${keyword}」的记录。` : '被驳回的订单、客户、额度或成本修正申请会出现在这里；可编辑重提或取消申请。'}
						/>
					) : (
						<AdvanceTableResponsive
							headerClassName='bg-200 text-nowrap align-middle font-sans-serif'
							rowClassName='btn-reveal-trigger text-nowrap align-middle'
							tableProps={{
								className: 'fs-10 fw-semibold mb-0 overflow-hidden',
							}}
						/>
					)}
					{showFooter ? <PendingListTableFooter showTableControls={displayOrders.length > 0} /> : null}
				</PendingListToolbar>
			</AdvanceTableProvider>

			<SystemAlert
				title='取消申请确认'
				bodyText={`确定取消「${cancelLabel}」吗？取消后不再出现在被驳回待办，审批履历仍保留。`}
				cancelText='返回'
				confirmText='确定取消'
				confirmColor='primary'
				confirmWaiting={isCancelling}
				modalShow={showCancelConfirm}
				setModalShow={setShowCancelConfirm}
				handleConfirm={handleCancelConfirm}
			/>

			{renewCustomer ? (
				<CustomerCreateApplyModal
					key={renewCustomer._id}
					applyModalShow={showRenewModal}
					setApplyModalShow={(show) => {
						setShowRenewModal(show);
						if (!show) setRenewCustomer(null);
					}}
					applicant={renewApplicant}
					initialValues={renewInitialValues}
					resubmittedFrom={renewCustomer._id}
					rejectionInfo={{
						approverName: renewCustomer.approver?.name || '',
						rejectedAt: renewCustomer.updatedAt,
						reason: String(renewCustomer.approvalSuggestions || '').trim(),
					}}
					onCancelApplication={() => {
						const approval = renewCustomer;
						setShowRenewModal(false);
						setRenewCustomer(null);
						requestCancel({
							_id: approval._id,
							entryKind: 'customer',
							approval,
							title: approval.title,
						});
					}}
					onSubmitted={(res) => {
						setShowRenewModal(false);
						setRenewCustomer(null);
						refetchMyRejectedOrders?.();
						if (res?._id) {
							navigate(`/approval/customer/detail/${res._id}`);
						}
					}}
				/>
			) : null}
			{renewQuota ? (
				<QuotaReapplyModal
					show
					onHide={() => setRenewQuota(null)}
					approval={renewQuota}
					onSubmitted={() => {
						setRenewQuota(null);
						refetchMyRejectedOrders?.();
					}}
				/>
			) : null}
			<FinalCostApplyInfoModal
				show={Boolean(finalCostApplyInfo)}
				onHide={() => setFinalCostApplyInfo(null)}
				approval={finalCostApplyInfo?.approval}
				onResolved={() => {
					setFinalCostApplyInfo(null);
					refetchMyRejectedOrders?.();
				}}
			/>
			<FinalCostReapplyModal
				show={Boolean(renewFinalCost)}
				onHide={() => setRenewFinalCost(null)}
				approval={renewFinalCost}
				onSubmitted={() => {
					setRenewFinalCost(null);
					setFinalCostApplyInfo(null);
					refetchMyRejectedOrders?.();
				}}
			/>
			<QuotaApplyInfoModal
				show={Boolean(quotaApplyInfo)}
				onHide={() => setQuotaApplyInfo(null)}
				approval={quotaApplyInfo?.approval}
				onRenew={() => {
					const row = quotaApplyInfo?.row;
					setQuotaApplyInfo(null);
					if (row) openQuotaRenew(row);
				}}
				onCancel={() => {
					const row = quotaApplyInfo?.row;
					setQuotaApplyInfo(null);
					if (row) requestCancel(row);
				}}
			/>
			<CustomerApplyInfoModal
				show={Boolean(customerApplyInfo)}
				onHide={() => setCustomerApplyInfo(null)}
				approval={customerApplyInfo?.approval}
				onRenew={() => {
					const row = customerApplyInfo?.row;
					setCustomerApplyInfo(null);
					if (row) openCustomerRenew(row);
				}}
				onCancel={() => {
					const row = customerApplyInfo?.row;
					setCustomerApplyInfo(null);
					if (row) requestCancel(row);
				}}
			/>
		</>
	);
};

export default RejectedOrderPendings;
